import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { configApi } from '../../services/configApi';
import { DeploymentResult, DeploymentHistoryResponse } from '../../types/config';

interface DeploymentStatusProps {
  deviceId: number;
  currentDeployment?: DeploymentResult;
  onDeploymentUpdate?: (deployment: DeploymentResult) => void;
}

const DeploymentStatus: React.FC<DeploymentStatusProps> = ({
  deviceId,
  currentDeployment,
  onDeploymentUpdate
}) => {
  const [deployments, setDeployments] = useState<DeploymentResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState<DeploymentResult | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [hideSuccessAlert, setHideSuccessAlert] = useState(false);

  const loadDeployments = async () => {
    try {
      setLoading(true);
      const response: DeploymentHistoryResponse = await configApi.getDeploymentHistory(deviceId, 10);
      setDeployments(response.deployments);
      
      // Update current deployment if it exists and check if status has changed
      if (currentDeployment && onDeploymentUpdate) {
        const updatedCurrentDeployment = response.deployments.find(
          dep => dep.deployment_id === currentDeployment.deployment_id
        );
        
        if (updatedCurrentDeployment && updatedCurrentDeployment.status !== currentDeployment.status) {
          onDeploymentUpdate(updatedCurrentDeployment);
        }
      }
    } catch (error) {
      console.error('Failed to load deployment history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeployment = async (deploymentId: string) => {
    try {
      setCancelling(deploymentId);
      await configApi.cancelDeployment(deviceId, deploymentId);
      await loadDeployments(); // Refresh the list
    } catch (error) {
      console.error('Failed to cancel deployment:', error);
    } finally {
      setCancelling(null);
    }
  };

  const handleViewDetails = async (deployment: DeploymentResult) => {
    try {
      // Get latest status
      const updatedDeployment = await configApi.getDeploymentStatus(deviceId, deployment.deployment_id);
      setSelectedDeployment(updatedDeployment);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Failed to get deployment status:', error);
      setSelectedDeployment(deployment);
      setShowDetailDialog(true);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'timeout':
        return <ErrorIcon color="warning" />;
      case 'cancelled':
        return <CancelIcon color="action" />;
      case 'in_progress':
        return <CircularProgress size={20} />;
      case 'pending':
        return <HourglassEmptyIcon color="info" />;
      default:
        return <HourglassEmptyIcon color="action" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
      case 'timeout':
        return 'error';
      case 'cancelled':
        return 'default';
      case 'in_progress':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  useEffect(() => {
    loadDeployments();
  }, [deviceId]);

  // Auto-hide success alert after 8 seconds
  useEffect(() => {
    if (currentDeployment?.status === 'success' && !hideSuccessAlert) {
      const timer = setTimeout(() => {
        setHideSuccessAlert(true);
      }, 8000);
      return () => clearTimeout(timer);
    }
    
    // Reset hide flag when currentDeployment changes
    if (currentDeployment?.status !== 'success') {
      setHideSuccessAlert(false);
    }
  }, [currentDeployment, hideSuccessAlert]);

  // Auto-refresh for in-progress deployments
  useEffect(() => {
    const hasInProgressDeployment = deployments.some(
      dep => dep.status === 'in_progress' || dep.status === 'pending'
    );

    const currentDeploymentInProgress = currentDeployment && 
      (currentDeployment.status === 'in_progress' || currentDeployment.status === 'pending');

    if (hasInProgressDeployment || currentDeploymentInProgress) {
      const interval = setInterval(loadDeployments, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [deployments, currentDeployment]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Deployment Status</Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadDeployments}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Current Deployment Alert */}
      {currentDeployment && !(currentDeployment.status === 'success' && hideSuccessAlert) && (
        <Alert 
          severity={
            currentDeployment.status === 'success' ? 'success' :
            currentDeployment.status === 'in_progress' ? 'info' :
            currentDeployment.status === 'failed' || currentDeployment.status === 'timeout' ? 'error' :
            'warning'
          }
          sx={{ mb: 2 }}
          action={
            (currentDeployment.status === 'in_progress' || currentDeployment.status === 'pending') && (
              <Button
                color="inherit"
                size="small"
                onClick={() => handleCancelDeployment(currentDeployment.deployment_id)}
                disabled={cancelling === currentDeployment.deployment_id}
                startIcon={cancelling === currentDeployment.deployment_id ? <CircularProgress size={16} /> : <StopIcon />}
              >
                Cancel
              </Button>
            )
          }
        >
          <Typography variant="body2">
            <strong>Latest Deployment:</strong> {currentDeployment.message}
          </Typography>
          {currentDeployment.status === 'in_progress' && (
            <LinearProgress sx={{ mt: 1 }} />
          )}
        </Alert>
      )}

      {/* Deployment History Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Deployment ID</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Timestamp</TableCell>
              <TableCell>Attempts</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : deployments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No deployments found
                </TableCell>
              </TableRow>
            ) : (
              deployments.map((deployment) => (
                <TableRow key={deployment.deployment_id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getStatusIcon(deployment.status)}
                      <Chip
                        label={deployment.status}
                        color={getStatusColor(deployment.status) as any}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {deployment.deployment_id.slice(-8)}
                    </Typography>
                  </TableCell>
                  <TableCell>{deployment.config_version}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatTimestamp(deployment.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>{deployment.attempts}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                      {deployment.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(deployment)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(deployment.status === 'in_progress' || deployment.status === 'pending') && (
                        <Tooltip title="Cancel Deployment">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelDeployment(deployment.deployment_id)}
                            disabled={cancelling === deployment.deployment_id}
                          >
                            {cancelling === deployment.deployment_id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <StopIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Deployment Details Dialog */}
      <Dialog 
        open={showDetailDialog} 
        onClose={() => setShowDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Deployment Details
        </DialogTitle>
        <DialogContent>
          {selectedDeployment && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Deployment ID: {selectedDeployment.deployment_id}
              </Typography>
              
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                <Box>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getStatusIcon(selectedDeployment.status)}
                    <Chip
                      label={selectedDeployment.status}
                      color={getStatusColor(selectedDeployment.status) as any}
                      size="small"
                    />
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="textSecondary">Config Version</Typography>
                  <Typography variant="body1">{selectedDeployment.config_version}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="textSecondary">Attempts</Typography>
                  <Typography variant="body1">{selectedDeployment.attempts}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="textSecondary">Timestamp</Typography>
                  <Typography variant="body1">{formatTimestamp(selectedDeployment.timestamp)}</Typography>
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">MQTT Topic</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                  {selectedDeployment.mqtt_topic}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Message</Typography>
                <Typography variant="body1">{selectedDeployment.message}</Typography>
              </Box>

              {selectedDeployment.error_details && (
                <Box>
                  <Typography variant="body2" color="textSecondary">Error Details</Typography>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {selectedDeployment.error_details}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeploymentStatus; 